/* eslint-disable indent */
/*
 *
 * ProcessingRulesForm
 *
 * @version 1.0.0
 * @author Alessandro Mosca
 * @since 23-Jan-2025
 */

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import PortalSelect from '@nexidigital/portalselect'
import PortalSwitch from '@nexidigital/portalswitch'
import PortalTextInput from '@nexidigital/portaltextinput'
import './ProcessingRulesForm.scss'
import { maskedPanValidator, addTimeSymbol } from 'Utility/validatorForm'
import { PROCESSING_RULES_TYPES, CARD_TYPE } from 'Schemas/constants'
import { captions, messages } from './languageModule'
import './ProcessingRulesForm.scss'

const ProcessingRulesForm = ({ formik, selectedRule, action, actions }) => {
  const intl = useIntl()
  const [maskedPan, setmaskedPan] = useState('')
  const isAmex = maskedPan.startsWith('34') || maskedPan.startsWith('37')

  useEffect(() => {
    formik.setValues((prevValues) => ({
      ...prevValues,
      value: prevValues.value
    }))
  }, [formik.values.value])

  useEffect(() => {
    if (action === 'create') {
      actions.resetSelectedRule()
      return
    }

    if (!selectedRule) return

    formik.setValues({
      ...formik.values,
      ruleName: selectedRule.name || '',
      processingRulesType: selectedRule.ruleType || '',
      processingRulesStatus: selectedRule.active || false,
      value: selectedRule.value || ''
    })
  }, [selectedRule])

  const handleMaskedPanChange = (e) => {
    setmaskedPan(e.target.value)
    const value = maskedPanValidator(e)
    formik.setFieldValue('value', value)
  }

  const handleChange = (name, value) => {
    if (!/^[0-9.,]*$/.test(value)) return
    formik.setFieldValue(name, value)
  }

  const handleBlur = (name, value) => {
    formik.setFieldValue(name, value)
    formik.setFieldTouched(name, true)
  }

  const handleCardHolderChange = (name, value) => {
    if (!/^[a-zA-ZÀ-ÖØ-öø-ÿ\s]*$/.test(value)) return
    formik.setFieldValue(name, value)
  }

  const handleAutoLockoutTimesChange = (name, e) => {
    const value = e.target.value

    // Permetti solo numeri e i caratteri ":"
    if (!/^[0-9:]*$/.test(value)) return

    // Aggiungi ":" se necessario
    const formattedValue = addTimeSymbol(value)

    formik.setFieldValue(name, formattedValue)
  }

  const dynamicFields = (ruleTypeSetted) => {
    switch (ruleTypeSetted) {
      case 'CARD_BIN_BLOCK':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.CARD_BIN_BLOCK)}
            id='textInputCardBinBlock'
            type='text'
            maxLength={8}
            onChange={(e) => handleChange('value', e.target.value)}
            onBlur={(e) => handleBlur('value', e.target.value)}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholderBlank
          />
        )
      case 'SINGLE_PAN_BLOCK':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.SINGLE_PAN_BLOCK)}
            id='textInputSinglePanBlock'
            type='text'
            maxLength={19}
            onChange={(e) => handleChange('value', e.target.value)}
            onBlur={(e) => handleBlur('value', e.target.value)}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholderBlank
          />
        )
      case 'MASKED_PAN':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.MASKED_PAN)}
            id='textInputMaskedPan'
            type='text'
            maxLength={isAmex ? 15 : 16}
            onChange={(e) => {
              handleMaskedPanChange(e)
            }}
            onBlur={(e) => handleBlur('value', e.target.value)}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholderBlank
          />
        )
      case 'CARD_TYPE':
        return (
          <PortalSelect
            id='selectCardType'
            options={CARD_TYPE?.map((a) => ({ value: a, label: intl.formatMessage(captions[a]) }))}
            label={intl.formatMessage(captions.CARD_TYPE)}
            ariaLabelIconButton={intl.formatMessage(captions.CARD_TYPE)}
            value={formik.values.value || ''}
            onChange={(option) => {
              formik.setFieldValue('value', option.value)
            }}
            noSearch
            placeholderBlank
          />
        )
      case 'IP_BLOCK':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.IP_BLOCK)}
            id='textInputIpBlock'
            type='text'
            labelIcon='info'
            labelTooltip={intl.formatMessage(captions.IP_TOOLTIP)}
            maxLength={18}
            {...formik.getFieldProps('value')}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholderBlank
          />
        )
      case 'CARDHOLDER_BLOCK':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.CARDHOLDER_BLOCK)}
            id='textInputBlockCardHolder'
            type='text'
            maxLength={255}
            onChange={(e) => handleCardHolderChange('value', e.target.value)}
            onBlur={(e) => handleBlur('value', e.target.value)}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholderBlank
          />
        )
      case 'SETTING_AUTO_LOCKOUT_TIMES':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.SETTING_AUTO_LOCKOUT_TIMES)}
            id='textInputSettingAutoLockoutTimes'
            type='text'
            maxLength={8}
            onChange={(e) => {
              handleAutoLockoutTimesChange('value', e)
            }}
            onBlur={(e) => handleBlur('value', e.target.value)}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholder='hh:mm:ss'
          />
        )
      case 'SETTING_DUPLICATE_LOCKOUT_TIMES':
        return (
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
            label={intl.formatMessage(captions.SETTING_DUPLICATE_LOCKOUT_TIMES)}
            id='textInputSettingDuplicateLockoutTimes'
            type='text'
            maxLength={8}
            onChange={(e) => {
              handleAutoLockoutTimesChange('value', e)
            }}
            onBlur={(e) => handleBlur('value', e.target.value)}
            error={!!(formik.touched.value && formik.errors.value)}
            errorText={formik.errors.value}
            value={formik.values.value || ''}
            placeholder='hh:mm:ss'
          />
        )
    }
  }

  return (
    <div className='processingRulesForm_container'>
      <div className='form-row switch'>
        <PortalSwitch
          id='switchProcessingRuleStatus'
          label={intl.formatMessage(captions.STATUS)}
          ariaLabel={intl.formatMessage(captions.STATUS)}
          checked={formik.values.processingRulesStatus}
          activeLabel={intl.formatMessage(captions.ENABLED)}
          deactiveLabel={intl.formatMessage(captions.DISABLED)}
          onChange={(selectedOption) => formik.setFieldValue('processingRulesStatus', selectedOption)}
          error={formik.touched.processingRulesStatus && formik.errors.processingRulesStatus}
          errorText={formik.errors.processingRulesStatus}
        />
      </div>
      <div className='form-row'>
        <PortalTextInput
          ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)}
          label={intl.formatMessage(captions.RULE_NAME)}
          id='textInputRuleName'
          type='text'
          {...formik.getFieldProps('ruleName')}
          maxLength={255}
          error={!!(formik.touched.ruleName && formik.errors.ruleName)}
          errorText={formik.errors.ruleName}
          value={formik.values.ruleName || ''}
          placeholderBlank
        />

        <PortalSelect
          id='selectProcessingRuleType'
          disabled={action === 'manage'}
          options={PROCESSING_RULES_TYPES?.map((a) => ({ value: a, label: intl.formatMessage(captions[a]) }))}
          label={intl.formatMessage(captions.RULE_TYPE)}
          ariaLabelIconButton={intl.formatMessage(captions.RULE_TYPE)}
          value={formik.values.processingRulesType || ''}
          onChange={(option) => {
            formik.setFieldValue('value', '')
            formik.setFieldValue('processingRulesType', option.value)
          }}
          noSearch
          placeholderBlank
        />
        {dynamicFields(formik.values.processingRulesType)}
      </div>
    </div>
  )
}

ProcessingRulesForm.propTypes = {
  // Add here some propTypes
  formik: PropTypes.object.isRequired,
  selectedRule: PropTypes.object,
  action: PropTypes.string,
  actions: PropTypes.object
}

ProcessingRulesForm.defaultProps = {
  selectedRule: {},
  action: '',
  actions: {}
}

export default ProcessingRulesForm
